import { Suspense, useState } from 'react';

import { ChevronRightIcon } from '@heroicons/react/20/solid';

import { Card } from '@/components/Card';
import { Loader } from '@/components/Loader';
import { ActiveFiltersBar } from '@/components/filters/ActiveFiltersBar';
import { FiltersButton } from '@/components/filters/FiltersButton';
import { Container } from '@/components/layout/Container';
import { OrderForecastGraphs } from '@/features/forecasts/components/explorer/OrderForecastGraphs';
import { OrderForecastSummary } from '@/features/forecasts/components/explorer/OrderForecastSummary';
import { ForecastFiltersDrawer } from '@/features/forecasts/components/explorer/filters/ForecastFiltersDrawer';
import { useForecastFilterControls } from '@/features/forecasts/hooks/useForecastFilterControls';
import { useTenant } from '@/providers/TenantProvider';

export function ForecastExplorerContainer() {
  const { grains } = useTenant();
  const {
    appliedFilters,
    draftFilters,
    setDraftFilters,
    facets,
    filtersOpen,
    setFiltersOpen,
    filterChips,
    filterCount,
    openFilters,
    applyDraft,
    clearAppliedFilters,
  } = useForecastFilterControls();

  const [appliedFiltersOpen, setAppliedFiltersOpen] = useState(false);

  const renderGraphs = !(
    // ship_to_geography and ship_from are not yet required filter dimensions — re-enable
    // these guards once those grains are surfaced in the filter drawer and populated by users.
    // (!!grains.ship_to_geography && !(appliedFilters.shipToGeography || 'North America')) ||
    ((!!grains.ship_to && !appliedFilters.customer) || (!!grains.product && !appliedFilters.product))
    // (!!grains.ship_from && !appliedFilters.shipFrom)
  );

  return (
    <>
      <ForecastFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        draft={draftFilters}
        setDraft={setDraftFilters}
        facets={facets}
        onApply={applyDraft}
        onClear={clearAppliedFilters}
      />

      {renderGraphs ? (
        <div className="space-y-6">
          <Suspense
            fallback={
              <Container>
                <Loader containerClass="w-full" />
              </Container>
            }
          >
            <OrderForecastSummary />
          </Suspense>
          <Suspense
            fallback={
              <Container>
                <Loader containerClass="w-full" />
              </Container>
            }
          >
            <OrderForecastGraphs
              actions={
                <FiltersButton onClick={openFilters} className="py-2">
                  Filters
                  {filterCount > 0 && (
                    <span className="rounded-full bg-[#274483]/10 px-2 py-0.5 text-xs font-semibold text-[#274483]">
                      {filterCount}
                    </span>
                  )}
                </FiltersButton>
              }
            />
          </Suspense>

          {filterChips.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setAppliedFiltersOpen((v) => !v)}
                aria-expanded={appliedFiltersOpen}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700"
              >
                <ChevronRightIcon className={`h-4 w-4 transition-transform ${appliedFiltersOpen ? 'rotate-90' : ''}`} />
                Applied filters
                <span className="rounded-full bg-[#274483]/10 px-2 py-0.5 text-xs font-semibold text-[#274483]">
                  {filterChips.length}
                </span>
              </button>
              {appliedFiltersOpen && (
                <div className="px-3 pb-3">
                  <ActiveFiltersBar
                    chips={filterChips}
                    onClear={clearAppliedFilters}
                    className="mt-0"
                    showLabel={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Card>
          <h3 className="text-center text-md font-bold">Select filters to explore forecasts</h3>
        </Card>
      )}
    </>
  );
}
